import { Context, createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { UseQueryExecute } from 'urql';

import { useCurrentUserQuery } from 'generated/hooks';

import { useTelegramMiniApp } from 'app/_context/TelegramContext';
import { identifyUser, trackOverridePlatforms } from 'app/_tracking';
import { CurrentUserType } from 'types';

type AuthContextType = {
  currentUser?: CurrentUserType | null;
  reFetchCurrentUser: UseQueryExecute,
};

const defaultValues: AuthContextType = {
  currentUser: null,
  reFetchCurrentUser: () => void 0
};

export const ClientAuthContext: Context<AuthContextType> = createContext(defaultValues);

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [res, reFetchCurrentUser] = useCurrentUserQuery();
  const { isEnabledTWA } = useTelegramMiniApp();

  const [currentUser, setCurrentUserRaw] = useState(() => {
    return res?.data?.currentUser;
  });

  const setCurrentUser = useCallback((user: CurrentUserType) => {
    setCurrentUserRaw(user);
    identifyUser(user, trackOverridePlatforms(isEnabledTWA));
  }, [isEnabledTWA]);

  useMemo(() => {
    if (!res.data?.currentUser) return null;
    setCurrentUser(res.data.currentUser);
    return res.data;
  }, [res.data, setCurrentUser]);

  return (
    <ClientAuthContext.Provider
      value={{
        currentUser,
        reFetchCurrentUser
      }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(ClientAuthContext);
}
